import React, { FC, useCallback, useContext, useState } from 'react';
import { useDropzone } from 'react-dropzone';

import FileArrowUp from '@gravity-ui/icons/FileArrowUp';
import { Icon, Loader } from '@gravity-ui/uikit';
import i18n from '@yandex-int/i18n';
import clx from 'classnames';
import { uploadImage } from 'client/api';
import { CurrentAliceAppContext } from 'client/context/aliceAppContext';
import { FormErrors } from 'client/hocs/types';
import { InstallableAppFormSettings } from 'client/types';
import { translateError } from 'client/utils/errorMessage';
import { ACCEPTED_LOGO_FORMATS } from 'client/utils/settings/common';
import * as appFormKeyset from 'i18n/AppForm';
import * as keyset from 'i18n/Settings';

import styles from './FilesUploaders.module.scss';

const t = i18n(keyset);
const tAppForm = i18n(appFormKeyset);

interface Props {
    disabled: boolean;
    logoUrl: string | null;
    setErrors: (errors: FormErrors<InstallableAppFormSettings & object>, cb?: () => void) => void;
    onIdChange: (logoUrl: string) => void;
    onUrlChange: (logoUrl: string) => void;
}

const createAliceIcon = (logoUrl: string, isLoading: boolean, isDragActive: boolean, disabled: boolean) => {
    if (isLoading) {
        return (
            <div className={clx(styles.skillIcon__container, styles.skillIcon_loading)}>
                <Loader size="s" />
            </div>
        );
    }
    if (isDragActive) {
        return (
            <div className={clx(styles.skillIcon__container, styles.skillIcon_empty)}>
                <div>{tAppForm('load-here')}</div>
            </div>
        );
    }
    if (logoUrl)
        return (
            <>
                <img className={styles.skillIcon__shadow} src={logoUrl} />
                <div
                    className={clx(
                        styles.skillIcon__container,
                        styles.skillIcon_preview,
                        disabled && styles.skillIcon__container_disabled,
                    )}
                    style={{ backgroundImage: `url(${logoUrl})` }}
                />
            </>
        );
    return (
        <div
            className={clx(
                styles.skillIcon__container,
                styles.skillIcon_empty,
                disabled && styles.skillIcon__container_disabled,
            )}
        >
            <Icon data={FileArrowUp} size={15} />
            <div className={styles.skillIcon__resolution}>{'224x224'}</div>
            <div className={styles.skillIcon__label}>{t('icon')}</div>
        </div>
    );
};

export const AliceSkillLogoUploader: FC<Props> = ({ disabled, logoUrl, onIdChange, onUrlChange, setErrors }) => {
    const [isLoading, setIsLoading] = useState(false);
    const { aliceApp } = useContext(CurrentAliceAppContext)!;

    const onDrop = useCallback((acceptedFiles) => {
        setIsLoading(true);

        uploadImage(aliceApp.id, acceptedFiles[0])
            .then((image) => {
                onIdChange(image.id);
                onUrlChange(image.url);
                setIsLoading(false);
            })
            .catch((err) => {
                const { message } = err;
                setErrors({ logoId: translateError(message) });
                setIsLoading(false);
            });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        onDrop,
        disabled,
        accept: [...ACCEPTED_LOGO_FORMATS],
    });

    return (
        <div
            className={clx(styles.uploaderContainer, disabled && styles.uploaderContainer_disabled)}
            {...getRootProps()}
        >
            <input {...getInputProps()} />
            {createAliceIcon(logoUrl || '', isLoading, isDragActive, disabled)}
        </div>
    );
};
